import React, { useEffect } from 'react';
import { Form, Col, Row, Container } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, string } from '../../Helpers/Validators/customValidators';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import Button from '@material-ui/core/Button';
import SvgIcon from '@material-ui/core/SvgIcon';
import SmokingRoomsIcon from '@material-ui/icons/SmokingRooms';
import { SelectInput, MultiSelectInput } from '../../Helpers/Inputs';
import getMonth from '../../Helpers/Lists/Months';

import './TobaccoUse.scss';

export default function TobaccoUse(props) {
  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: TobaccoUse.ValidationSchema(props.ageSpecs),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
  };

  const getTobaccoTypeSelectOptions = () => {
    return [
      { value: 'Cigarettes/E-Cigarettes', label: 'Cigarettes/E-Cigarettes' },
      { value: 'Cigars', label: 'Cigars' },
      { value: 'Pipe', label: 'Pipe' },
      { value: 'Chew/Dip', label: 'Chew/Dip' },
      { value: 'Snuff', label: 'Snuff' },
      { value: 'Nicotine Patch/Gum/Pill', label: 'Nicotine Patch/Gum/Pill' },
      { value: 'Other', label: 'Other' },
    ];
  };

  const getOftenUsedSelectOptions = () => {
    return [
      { value: 'Daily', label: 'Daily' },
      { value: 'Weekly', label: 'Weekly' },
      { value: 'Monthly', label: 'Monthly' },
      { value: 'Celebratory', label: 'Celebratory' },
    ];
  };

  const getYearsSelectOptions = () => {
    let years = [];
    let yearStartBoundary;
    let yearEndBoundary;
    const basicInfoTobacco = props.basicInfo.tobacco;
    const birthdateYear = new Date(props.basicInfo.birthdate).getFullYear();
    const currentYear = new Date().getFullYear();

    switch(basicInfoTobacco) {
      case "Within the last year":
        yearStartBoundary = (currentYear - 1)
        yearEndBoundary = currentYear
        break;
      case "Within the last 1 to 3 years":
        yearStartBoundary = (currentYear - 3)
        yearEndBoundary = (currentYear - 1)
        break;
      case "Within the last 3 to 5 years":
        yearStartBoundary = (currentYear - 5)
        yearEndBoundary = (currentYear - 3)
        break;
      default:
        yearStartBoundary = birthdateYear;
        yearEndBoundary = currentYear;
        break;
    }

    for (let index = yearStartBoundary; index <= yearEndBoundary; index++) {
      years.push({ "value": index, "label": index })
    }

    return years;
  };

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'typeOfTobacco' });
    setValue('typeOfTobacco', props.formState.typeOfTobacco);
    register({ name: 'oftenUse' });
    setValue('oftenUse', props.formState.oftenUse);
    register({ name: 'month' });
    setValue('month', props.formState.month);
    register({ name: 'year' });
    setValue('year', props.formState.year);
  }, [register]);

  const onSubmit = () => {
    props.onNext();
  };

  return (
    <Container className="tobaccoUseContainer personalize-step">
      <Form onSubmit={handleSubmit(onSubmit)} className="tobaccoForm">
        <Row className="justify-content-center">
          <Col lg={6} md={10}>
            <div className="formFieldsContainer mx-auto">
              <div className="text-center mb-2">
                <SvgIcon component={SmokingRoomsIcon} className="icons" fontSize="large" color="primary" />
              </div>

              <p className="title mx-auto">Tobacco Use</p>
              <p className="subTitle">I last used tobacco/ nicotine on</p>

              <Row>
                <Col className="pr-1">
                  <Form.Group>
                    <SelectInput
                      name="month"
                      id="month"
                      value={props.formState.month}
                      onChange={(e) => handleValueChange('month', e.target.value)}
                      label="Month"
                      inputRef={register}
                      selectOptions={getMonth()}
                      errors={errors && errors.month}
                    />
                  </Form.Group>
                </Col>
                <Col className="pl-1">
                  <Form.Group>
                    <SelectInput
                      name="year"
                      id="year"
                      value={props.formState.year}
                      onChange={(e) => handleValueChange('year', e.target.value)}
                      label="Year"
                      inputRef={register}
                      selectOptions={getYearsSelectOptions()}
                      errors={errors && errors.year}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group>
                <MultiSelectInput
                  name="typeOfTobacco"
                  id="typeOfTobacco"
                  value={props.formState.typeOfTobacco ? props.formState.typeOfTobacco : []}
                  onChange={(e) => handleValueChange('typeOfTobacco', e.target.value)}
                  label="What type of tobacco or nicotine?"
                  inputRef={register}
                  selectOptions={getTobaccoTypeSelectOptions()}
                  errors={errors && errors.typeOfTobacco}
                />
              </Form.Group>

              <Form.Group>
                <SelectInput
                  name="oftenUse"
                  id="oftenUse"
                  value={props.formState.oftenUse}
                  onChange={(e) => handleValueChange('oftenUse', e.target.value)}
                  label="How often do/did you use?"
                  inputRef={register}
                  selectOptions={getOftenUsedSelectOptions()}
                  errors={errors && errors.oftenUse}
                />
              </Form.Group>
            </div>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col className="text-left pb-3 btn-back">
            <Button variant="outlined" size="medium" color="primary" onClick={props.onBack}>
              BACK
            </Button>
          </Col>
          <Col className="text-right pb-3">
            <Button variant="contained" size="medium" color="primary" type="submit">
              NEXT
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
}

TobaccoUse.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

TobaccoUse.ValidationSchema = () => {
  return object().shape({
    typeOfTobacco: string().required('Type Of Tobbaco is required.'),
    oftenUse: string().required('Often Use is required.'),
    month: string().required('Month is required.'),
    year: string().required('Year is required.'),
  });
};

TobaccoUse.DefaultFormState = (prefill = false) => {
  return {
    typeOfTobacco: prefill ? ['Cigarettes'] : [],
    oftenUse: prefill ? 'Daily' : '',
    month: prefill ? 'january' : '',
    year: prefill ? '2020' : '',
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Common/TobaccoUse.js